import { Button, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  DocumentCategory,
  NoticeDocumentType,
  StatusCode,
} from '../../../../../../api/enumerations';
import { requestNoticeCompanyDocument } from '../../../../../../api/theHive/notices';
import { CustomTextField } from '../../../../../../components/CustomInput';
import { ButtonBox } from '../../../../../../components/UI/Box';
import { WhiteCircularProgress } from '../../../../../../components/UI/Typography';
import { Constants } from '../../../../../../constants/hiveNotices';
import { GlobalContext } from '../../../../../../context/global';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { DialogContainer, OutlinedButton, StyledDialog } from '../styles';

interface RequestDocumentProps {
  dataCallback: () => Promise<void>;
  disabled: boolean;
}

export function RequestDocument({
  dataCallback,
  disabled,
}: RequestDocumentProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [description, setDescription] = useState('');

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => {
    setOpen(false);
    setDescription('');
  };

  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { request } = useParams();
  const requestId = parseInt(request as string, 10);
  const { getErrorMessage } = useErrorMessage();

  const handleSubmit = async (): Promise<void> => {
    if (description === '') {
      setSnackbarMessage('Adicione um nome para o documento solicitado.');
      setOpenSnackbar(true);
      setErrorMessage(true);
      return;
    }

    const formData = new FormData();
    formData.append('document_category', DocumentCategory.OTHER.toString());
    formData.append('description', description);
    formData.append('document_type', NoticeDocumentType.OTHERS.toString());

    try {
      setSubmitLoading(true);
      const response = await requestNoticeCompanyDocument(requestId, formData);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error(Constants.genericError);
      }

      setSnackbarMessage('Documento solicitado com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      dataCallback();
      handleClose();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <Button
        sx={{ backgroundColor: 'primary.main' }}
        onClick={handleOpen}
        disabled={disabled}
      >
        {Constants.newDocumentButton}
      </Button>
      <StyledDialog open={open} onClose={handleClose}>
        <DialogContainer>
          <Typography fontWeight={600}>Novo documento</Typography>
          <CustomTextField
            id="description"
            label="nome do documento"
            value={description}
            setValue={setDescription}
            maxLength={120}
            required
          />
          <ButtonBox gap={2} my={2.5}>
            <Button
              sx={{ width: '200px', height: '48px' }}
              disabled={submitLoading}
              onClick={handleSubmit}
            >
              {submitLoading ? (
                <WhiteCircularProgress size={22} />
              ) : (
                'solicitar'
              )}
            </Button>
            <OutlinedButton
              sx={{ maxWidth: '200px', width: '200px', height: '48px' }}
              disabled={submitLoading}
              onClick={handleClose}
            >
              cancelar
            </OutlinedButton>
          </ButtonBox>
        </DialogContainer>
      </StyledDialog>
    </>
  );
}
